import type { InferInsertModel, InferSelectModel } from 'drizzle-orm';
import { relations, sql } from 'drizzle-orm';
import {
  boolean,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { softDelete } from '../columns/soft-delete';
import { users } from './users';
export const termVersionKindEnum = pgEnum('term_version_kind', [
  'participation',
  'minor_authorization',
]);
export const termVersionStatusEnum = pgEnum('term_version_status', [
  'draft',
  'published',
  'archived',
]);

/**
 * Versões dos termos (participação / autorização de menor). Uma versão publicada nunca muda —
 * editar cria um rascunho `version + 1`; publicar arquiva a anterior. Regras em
 * `src/api/term-versions/term-version.service.ts`; guia na skill `terms-acceptance`.
 */
export const termVersions = pgTable(
  'term_versions',
  {
    id: uuid('id')
      .primaryKey()
      .$defaultFn(() => Bun.randomUUIDv7()),
    kind: termVersionKindEnum('kind').notNull(),
    version: integer('version').notNull(),
    title: text('title').notNull(),
    /** Documento do editor (Tiptap JSON) — fonte da verdade. */
    content: jsonb('content').notNull().$type<Record<string, unknown>>(),
    /** HTML renderizado de `content` (com as variáveis como marcadores), usado no site e no PDF. */
    contentHtml: text('content_html'),
    /** SHA-256 de `content_html`, calculado na publicação. */
    contentHash: text('content_hash'),
    status: termVersionStatusEnum('status').notNull().default('draft'),
    publishedAt: timestamp('published_at'),
    createdById: uuid('created_by_id').references(() => users.id),
    isActive: boolean('is_active').default(true),
    createdAt: timestamp('created_at')
      .notNull()
      .$defaultFn(() => new Date()),
    updatedAt: timestamp('updated_at')
      .$onUpdate(() => new Date())
      .notNull(),
    ...softDelete,
  },
  (table) => [
    // Índices únicos valem só entre não excluídos (soft delete).
    uniqueIndex('term_versions_kind_version_idx')
      .on(table.kind, table.version)
      .where(sql`${table.deletedAt} is null`),
    // No máximo uma versão vigente por tipo — garantido pelo banco, não só pelo service.
    uniqueIndex('term_versions_one_published_per_kind_idx')
      .on(table.kind)
      .where(sql`${table.status} = 'published' and ${table.deletedAt} is null`),
  ],
);

export const termVersionsRelations = relations(termVersions, ({ one }) => ({
  createdBy: one(users, {
    fields: [termVersions.createdById],
    references: [users.id],
  }),
}));

export type TermVersion = InferSelectModel<typeof termVersions>;
export type CreateTermVersionInput = InferInsertModel<typeof termVersions>;
export type UpdateTermVersionInput = Partial<CreateTermVersionInput>;
